import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Color helpers
const LEVEL_COLORS = {
  CRITICAL: { bg: "bg-red-50", text: "text-red-700", badge: "bg-red-600", border: "border-red-300" },
  HIGH:     { bg: "bg-orange-50", text: "text-orange-700", badge: "bg-orange-500", border: "border-orange-300" },
  ELEVATED: { bg: "bg-yellow-50", text: "text-yellow-700", badge: "bg-yellow-500", border: "border-yellow-300" },
  MODERATE: { bg: "bg-green-50", text: "text-green-700", badge: "bg-green-500", border: "border-green-300" },
  LOW:      { bg: "bg-emerald-50", text: "text-emerald-700", badge: "bg-emerald-500", border: "border-emerald-300" },
};
const lc = (level) => LEVEL_COLORS[level] || LEVEL_COLORS.MODERATE;

const POSTURE_COLORS = {
  DECLINE:     { bg: "bg-red-600", text: "text-red-700", border: "border-red-500" },
  CONDITIONAL: { bg: "bg-orange-500", text: "text-orange-700", border: "border-orange-500" },
  STANDARD:    { bg: "bg-blue-600", text: "text-blue-700", border: "border-blue-500" },
  PREFERRED:   { bg: "bg-emerald-600", text: "text-emerald-700", border: "border-emerald-500" },
};
const pc = (p) => POSTURE_COLORS[p] || POSTURE_COLORS.CONDITIONAL;

const CONFIDENCE_COLORS = {
  HIGH: "bg-emerald-500", MODERATE: "bg-yellow-500", LOW: "bg-red-500",
};

const RATING_COLORS = {
  Strong: "text-emerald-600", High: "text-emerald-600",
  Medium: "text-yellow-600",
  Weak: "text-red-600", Low: "text-red-600",
};
const rc = (r) => RATING_COLORS[r] || "text-gray-600";

const ARROW = {
  up_likelihood:  { icon: "\u25B2", color: "text-red-500",    label: "likelihood" },
  down_severity:  { icon: "\u25BC", color: "text-emerald-500", label: "severity" },
  neutral:        { icon: "\u25B6", color: "text-yellow-500",  label: "conditional" },
};

const TIER_COLORS = {
  dominant: "bg-red-600", secondary: "bg-yellow-500", contextual: "bg-slate-400",
};

// Sub-components
const SnapshotCard = ({ label, value, valueColor }) => (
  <div className="flex justify-between items-center px-4 py-3 bg-slate-50 rounded border-l-3 border-slate-800" data-testid={`snapshot-${label.replace(/\s/g,"-").toLowerCase()}`}>
    <span className="text-xs text-slate-500 uppercase tracking-wide">{label}</span>
    <span className={`text-sm font-bold ${valueColor || "text-slate-800"}`}>{value}</span>
  </div>
);

const RiskCard = ({ risk }) => {
  const c = lc(risk.risk_level);
  const tierColor = TIER_COLORS[risk.risk_tier] || TIER_COLORS.contextual;
  return (
    <div className={`border rounded-lg p-5 ${c.border} ${c.bg}`} data-testid={`risk-card-${risk.risk_id}`}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <span className="text-xs text-slate-500 font-mono">{risk.risk_id}</span>
          <h4 className="text-base font-bold text-slate-800">{risk.risk_title}</h4>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-[10px] font-bold text-white uppercase ${tierColor}`}>{risk.risk_tier}</span>
          <span className={`px-3 py-1 rounded text-xs font-bold text-white ${c.badge}`}>{risk.risk_level}</span>
        </div>
      </div>
      <div className="flex gap-6 text-xs text-slate-500 mb-3 flex-wrap">
        <span>Category: <strong className="text-slate-700">{risk.risk_category}</strong></span>
        <span>Likelihood: <strong className="text-slate-700">{risk.likelihood}</strong></span>
        <span>Impact: <strong className="text-slate-700">{risk.business_impact}</strong></span>
        <span>Direction: <strong className="text-slate-700">{risk.risk_direction}</strong></span>
      </div>
      <div className="bg-white/80 border-l-3 border-blue-500 p-3 rounded mb-3">
        <p className="text-sm text-slate-700 leading-relaxed">{risk.underwriting_statement}</p>
      </div>
      <p className="text-xs text-slate-500 italic mb-2">
        <strong>Insurance Relevance:</strong> {risk.insurance_relevance}
      </p>
      <p className="text-xs text-slate-700">
        <strong>Recommended Action:</strong> {risk.recommended_risk_action}
      </p>
    </div>
  );
};

// Main Component
const UnderwritingReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingEs, setDownloadingEs] = useState(false);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/underwriting-report/generate`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to generate the underwriting report");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/underwriting-report/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `underwriting_report_${data?.company_name || "company"}_${new Date().toISOString().split("T")[0]}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("Failed to download the PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadPDFEs = async () => {
    try {
      setDownloadingEs(true);
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/underwriting-report/pdf-es`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte_underwriting_${data?.company_name || "empresa"}_${new Date().toISOString().split("T")[0]}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("Failed to download the Spanish PDF");
    } finally {
      setDownloadingEs(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center p-12" data-testid="uw-loading">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800"></div>
      <span className="ml-4 text-slate-600">Generating Insurance-Grade Report...</span>
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center" data-testid="uw-error">
      <p className="text-red-700 font-medium">{error}</p>
      <button onClick={fetchReport} className="mt-3 px-4 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700">Retry</button>
    </div>
  );

  if (!data) return null;

  const snap = data.risk_snapshot || {};
  const levelColor = lc(data.risk_level);
  const postureColor = pc(data.underwriting_posture);
  const confidenceBg = CONFIDENCE_COLORS[data.underwriting_confidence] || "bg-yellow-500";

  return (
    <div className="space-y-6" data-testid="underwriting-report">

      {/* Header */}
      <div className="bg-slate-900 rounded-lg p-6 text-white flex justify-between items-center" data-testid="uw-header">
        <div>
          <div className="text-xs tracking-widest text-slate-400 uppercase mb-1">Insurance-Grade Cyber Risk Report</div>
          <h2 className="text-2xl font-bold">{data.company_name}</h2>
          <p className="text-sm text-slate-400 mt-1">Assessment Date: {data.assessment_date}</p>
        </div>
        <div className="text-right flex items-center gap-6">
          <div>
            <div className="text-xs text-slate-400 uppercase">CyberScore</div>
            <div className="text-4xl font-bold">{data.cyber_score == null ? "—" : data.cyber_score}<span className="text-lg text-slate-400">/100</span></div>
            {data.ratable === false && (
              <div className="text-[10px] text-amber-400 mt-1 max-w-[150px] leading-tight" data-testid="uw-score-not-representative">
                Not representative: insufficient coverage
              </div>
            )}
          </div>
          <div>
            <div className="text-xs text-slate-400 uppercase">Posture</div>
            <span className={`inline-block px-4 py-2 rounded text-sm font-bold text-white ${postureColor.bg}`}>{data.underwriting_posture}</span>
          </div>
          <button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm font-medium text-white disabled:opacity-50"
            data-testid="uw-download-pdf"
          >
            {downloading ? "Generating..." : "Download PDF"}
          </button>
          <button
            onClick={handleDownloadPDFEs}
            disabled={downloadingEs}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded text-sm font-medium text-white disabled:opacity-50"
            data-testid="uw-download-pdf-es"
            title="Download the Spanish version of this report"
          >
            {downloadingEs ? "Generating..." : "Download PDF (ES)"}
          </button>
        </div>
      </div>

      {/* Section 0: Decision Framing */}
      {/* NOT RATABLE banner (Etapa 0 - integridad) */}
      {data.ratable === false && (
        <div className="bg-red-50 border-2 border-red-600 rounded-lg p-5" data-testid="uw-not-ratable-banner">
          <h3 className="text-base md:text-lg font-bold text-red-700 uppercase tracking-wide mb-2">
            Not Ratable — Insufficient Assessment Coverage
          </h3>
          <p className="text-sm text-red-900 leading-relaxed mb-3">
            This account cannot be rated with the available data. <strong>This is not a risk-based decline.</strong>{" "}
            The findings listed are factual, but assessment coverage is not representative of the attack surface.
          </p>
          <div className="text-xs font-bold text-red-700 uppercase tracking-wider mb-1">Missing for rating</div>
          <ul className="list-disc list-inside text-sm text-red-900 space-y-1" data-testid="uw-missing-for-rating">
            {(data.missing_for_rating || []).map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}

      {/* Integrity violations (solo DEV) */}
      {(data.integrity_violations || []).length > 0 && (
        <div className="bg-orange-50 border border-orange-400 rounded-lg p-4" data-testid="uw-integrity-violations">
          <div className="text-sm font-bold text-orange-800 mb-2">
            Integrity audit: {data.integrity_violations.length} unsourced statement(s)
          </div>
          <ul className="list-disc list-inside text-xs text-orange-900 space-y-1">
            {data.integrity_violations.map((v, i) => <li key={i}>{v}</li>)}
          </ul>
        </div>
      )}

      <div className={`bg-white rounded-lg shadow p-6 border-2 ${postureColor.border}`} data-testid="uw-section-decision">
        <h3 className="text-lg font-bold text-slate-800 border-b-2 border-slate-800 pb-2 mb-4 uppercase tracking-wide">
          <span className="text-blue-600 mr-2">0.</span>Underwriting Decision Summary
        </h3>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Underwriting Posture</div>
            <span className={`text-2xl font-bold ${postureColor.text}`}>{data.underwriting_posture}</span>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Confidence Index</div>
            <span className={`inline-block px-4 py-1.5 rounded text-sm font-bold text-white ${confidenceBg}`}>{data.underwriting_confidence}</span>
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded p-4 text-sm text-slate-700 leading-relaxed mb-4">
          {data.underwriting_posture_rationale}
        </div>

        <div className="mb-4">
          <h4 className="text-sm font-bold text-slate-700 mb-2">Primary Loss Driver</h4>
          <div className="bg-amber-50 border border-amber-200 border-l-4 border-l-orange-500 rounded p-3 text-sm text-slate-700">
            {data.primary_loss_driver}
          </div>
        </div>

        {data.expected_loss_narrative ? (
          <div className="mb-4">
            <h4 className="text-sm font-bold text-slate-700 mb-2">Loss Scenario (qualitative)</h4>
            <div className="bg-amber-50 border border-amber-200 border-l-4 border-l-orange-500 rounded p-3 text-sm text-slate-700">
              {data.expected_loss_narrative}
            </div>
          </div>
        ) : null}

        <div>
          <h4 className="text-sm font-bold text-slate-700 mb-2">Confidence Rationale</h4>
          <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm text-slate-600 leading-relaxed">
            {data.underwriting_confidence_rationale}
          </div>
        </div>
      </div>

      {/* Section 0.1: Basis of Assessment & Data Coverage */}
      <div className="bg-white rounded-lg shadow p-6" data-testid="uw-section-basis">
        <h3 className="text-lg font-bold text-slate-800 border-b-2 border-slate-800 pb-2 mb-4 uppercase tracking-wide">
          <span className="text-blue-600 mr-2">0.1</span>Basis of Assessment &amp; Data Coverage
        </h3>
        <p className="text-xs text-slate-600 bg-slate-50 border-l-4 border-slate-400 p-3 mb-4 leading-relaxed">
          {data.assessment_basis}
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-800 text-white text-xs uppercase tracking-wider">
                <th className="px-4 py-3 text-left">Data Source</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Detail</th>
              </tr>
            </thead>
            <tbody>
              {(data.data_coverage || []).map((c, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"} data-testid={`uw-coverage-row-${i}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{c.source}</td>
                  <td className={`px-4 py-3 font-bold ${c.status === "ASSESSED" ? "text-emerald-600" : "text-slate-400"}`}>
                    {c.status === "ASSESSED" ? "ASSESSED" : "NOT ASSESSED"}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{c.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 1: Executive Underwriting Summary */}
      <div className="bg-white rounded-lg shadow p-6" data-testid="uw-section-1">
        <h3 className="text-lg font-bold text-slate-800 border-b-2 border-slate-800 pb-2 mb-4 uppercase tracking-wide">
          <span className="text-blue-600 mr-2">1.</span>Executive Underwriting Summary
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
          <SnapshotCard label="Cyber Risk Level" value={snap.cyber_risk_level} valueColor={lc(snap.cyber_risk_level).text} />
          <SnapshotCard label="Insurability Status" value={snap.insurability_status} />
          <SnapshotCard label="Risk Trend" value={snap.risk_trend} />
          <SnapshotCard label="Continuous Monitoring" value={snap.continuous_monitoring} />
          <SnapshotCard label="Governance Maturity" value={snap.governance_maturity} />
        </div>

        <div className="mb-4">
          <h4 className="text-sm font-bold text-slate-700 mb-2">Primary Exposure Drivers</h4>
          <div className="space-y-1">
            {(data.primary_exposure_drivers || []).map((d, i) => (
              <div key={i} className="px-3 py-2 bg-amber-50 border-l-3 border-amber-400 text-sm text-slate-700">{d}</div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-sm font-bold text-slate-700 mb-2">Underwriting Interpretation</h4>
          <div className="bg-slate-50 border border-slate-200 rounded p-4 text-sm text-slate-700 leading-relaxed">
            {data.underwriting_interpretation}
          </div>
        </div>
      </div>

      {/* Section 2: Insurability Indicators */}
      <div className="bg-white rounded-lg shadow p-6" data-testid="uw-section-2">
        <h3 className="text-lg font-bold text-slate-800 border-b-2 border-slate-800 pb-2 mb-4 uppercase tracking-wide">
          <span className="text-blue-600 mr-2">2.</span>Insurability Indicators
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-800 text-white text-xs uppercase tracking-wider">
                <th className="px-4 py-3 text-left">Indicator</th>
                <th className="px-4 py-3 text-left">Rating</th>
                <th className="px-4 py-3 text-left">Impact</th>
                <th className="px-4 py-3 text-center">Direction</th>
              </tr>
            </thead>
            <tbody>
              {(data.insurability_indicators || []).map((ind, i) => {
                const a = ARROW[ind.arrow] || ARROW.neutral;
                return (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"} data-testid={`uw-indicator-row-${i}`}>
                    <td className="px-4 py-3 font-medium text-slate-800">{ind.indicator}</td>
                    <td className="px-4 py-3">
                      {ind.assessed === false ? (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-400 text-white uppercase">Not Assessed</span>
                      ) : (
                        <span className={`font-bold ${rc(ind.rating)}`}>{ind.rating}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {ind.impact}
                      {ind.basis ? <div className="text-[10px] text-slate-400 mt-1">{ind.assessed === false ? "" : "Basis: "}{ind.basis}</div> : null}
                    </td>
                    <td className={`px-4 py-3 text-center font-medium ${a.color}`}>
                      {ind.assessed === false ? "" : `${a.icon} ${a.label}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 3: Underwriting Risk Register */}
      <div className="bg-white rounded-lg shadow p-6" data-testid="uw-section-3">
        <h3 className="text-lg font-bold text-slate-800 border-b-2 border-slate-800 pb-2 mb-4 uppercase tracking-wide">
          <span className="text-blue-600 mr-2">3.</span>Underwriting Risk Register
        </h3>
        <div className="space-y-4">
          {(data.risk_register || []).map((risk) => (
            <RiskCard key={risk.risk_id} risk={risk} />
          ))}
          {(!data.risk_register || data.risk_register.length === 0) && (
            <p className="text-sm text-slate-500 text-center py-6">No insurable risks identified in current assessment.</p>
          )}
        </div>
      </div>

      {/* Section 4: Risk Trajectory */}
      <div className="bg-white rounded-lg shadow p-6" data-testid="uw-section-4">
        <h3 className="text-lg font-bold text-slate-800 border-b-2 border-slate-800 pb-2 mb-4 uppercase tracking-wide">
          <span className="text-blue-600 mr-2">4.</span>Risk Trajectory
        </h3>

        {/* Heat bar */}
        <div className="mb-6">
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>CRITICAL</span><span>HIGH</span><span>ELEVATED</span><span>MODERATE</span><span>LOW RISK</span>
          </div>
          <div className="relative h-8 rounded-full overflow-hidden" style={{ background: "linear-gradient(to right, #c0392b 0%, #d35400 25%, #f39c12 45%, #27ae60 70%, #2ecc71 100%)" }}>
            <div className="absolute top-0 h-full flex flex-col items-center" style={{ left: "70%" }}>
              <div className="w-0.5 h-full bg-white opacity-80"></div>
            </div>
            <div className="absolute top-0 h-full flex items-center" style={{ left: `${data.cyber_score == null ? 0 : Math.min(Math.max(data.cyber_score, 2), 98)}%`, transform: "translateX(-50%)" }}>
              <div className="w-5 h-5 rounded-full bg-white border-3 border-slate-800 shadow-lg"></div>
            </div>
          </div>
          <div className="flex justify-between text-xs mt-1">
            <span className="text-slate-400">0</span>
            <span className="text-slate-400">100</span>
          </div>
          <div className="flex items-center gap-6 mt-3 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 rounded-full bg-white border-2 border-slate-800"></div>
              <span className="text-slate-600">CyberScore obtained: <strong className="text-slate-800">{data.cyber_score == null ? "— / 100 (no data)" : `${data.cyber_score}/100`}</strong></span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-0.5 bg-white border border-slate-300"></div>
              <span className="text-slate-600">Expected score (insurable): <strong className="text-slate-800">70/100</strong></span>
            </div>
          </div>
        </div>

        {/* Score trend */}
        <div className="bg-slate-50 rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold text-slate-700">CyberScore Trend</h4>
            <div className={`px-3 py-1 rounded text-xs font-bold text-white ${data.trend === "IMPROVING" ? "bg-emerald-500" : data.trend === "DETERIORATING" ? "bg-red-500" : "bg-yellow-500"}`}>
              Trend: {data.trend}
            </div>
          </div>

          {(() => {
            const history = data.score_history || [];
            if (history.length === 0) return <p className="text-sm text-slate-400 text-center py-4">No historical data available.</p>;

            const W = 560, H = 180, PL = 45, PR = 20, PT = 20, PB = 35;
            const chartW = W - PL - PR, chartH = H - PT - PB;
            const n = history.length;
            const points = history.map((h, i) => ({
              x: PL + (n === 1 ? chartW / 2 : (i / (n - 1)) * chartW),
              y: PT + chartH - (Math.max(0, Math.min(h.score, 100)) / 100) * chartH,
              score: h.score,
              date: (h.date || "").slice(-10),
            }));
            const lineD = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
            const areaD = lineD + ` L${points[points.length - 1].x},${PT + chartH} L${points[0].x},${PT + chartH} Z`;
            const threshY = PT + chartH - (70 / 100) * chartH;
            const yLabels = [0, 25, 50, 70, 100];

            return (
              <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxWidth: 600 }}>
                {yLabels.map(v => {
                  const y = PT + chartH - (v / 100) * chartH;
                  return <line key={v} x1={PL} y1={y} x2={W - PR} y2={y} stroke="#e2e8f0" strokeWidth="0.5" />;
                })}
                {yLabels.map(v => {
                  const y = PT + chartH - (v / 100) * chartH;
                  return <text key={v} x={PL - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#94a3b8">{v}</text>;
                })}
                <line x1={PL} y1={threshY} x2={W - PR} y2={threshY} stroke="#22c55e" strokeWidth="1.2" strokeDasharray="6 3" />
                <text x={W - PR + 2} y={threshY + 3} fontSize="8" fill="#16a34a" fontWeight="bold">Insurable: 70</text>
                <path d={areaD} fill="url(#areaGrad)" opacity="0.25" />
                <defs>
                  <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d={lineD} fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                {points.map((p, i) => {
                  const color = p.score >= 70 ? "#22c55e" : p.score >= 50 ? "#eab308" : p.score >= 30 ? "#f97316" : "#ef4444";
                  return (
                    <g key={i}>
                      <circle cx={p.x} cy={p.y} r="5" fill="white" stroke={color} strokeWidth="2.5" />
                      <text x={p.x} y={p.y - 10} textAnchor="middle" fontSize="11" fontWeight="bold" fill={color}>{p.score}</text>
                      <text x={p.x} y={PT + chartH + 14} textAnchor="middle" fontSize="8" fill="#94a3b8">{p.date}</text>
                    </g>
                  );
                })}
              </svg>
            );
          })()}
        </div>
      </div>

      {/* Section 5: Control Loss Effect */}
      <div className="bg-white rounded-lg shadow p-6" data-testid="uw-section-5">
        <h3 className="text-lg font-bold text-slate-800 border-b-2 border-slate-800 pb-2 mb-4 uppercase tracking-wide">
          <span className="text-blue-600 mr-2">5.</span>Control Loss Effect Assessment
        </h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-800 text-white text-xs uppercase tracking-wider">
              <th className="px-4 py-3 text-left">Domain</th>
              <th className="px-4 py-3 text-left">Loss Effect</th>
              <th className="px-4 py-3 text-left w-1/4">Basis (ISO 27001 SoA)</th>
            </tr>
          </thead>
          <tbody>
            {(data.control_maturity || []).map((m, i) => {
              const effect = m.loss_effect || m.insurance_effect || "";
              const isPositive = /reduces|strong|mature|positive/i.test(effect);
              const isNegative = /absent|maximizes|elevates|deficien/i.test(effect);
              const textColor = isPositive ? "text-emerald-600" : isNegative ? "text-red-600" : "text-yellow-600";
              return (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"} data-testid={`uw-maturity-row-${i}`}>
                  <td className="px-4 py-3 font-semibold text-slate-800">{m.domain}</td>
                  <td className={`px-4 py-3 ${m.status === "Not Assessed" ? "text-slate-400" : textColor}`}>
                    {m.status === "Not Assessed" && (
                      <span className="px-2 py-0.5 mr-2 rounded text-[10px] font-bold bg-slate-400 text-white uppercase">Not Assessed</span>
                    )}
                    {effect}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500" data-testid={`uw-maturity-basis-${i}`}>{m.basis || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Section 6: Continuous Monitoring Assurance */}
      <div className="bg-white rounded-lg shadow p-6" data-testid="uw-section-6">
        <h3 className="text-lg font-bold text-slate-800 border-b-2 border-slate-800 pb-2 mb-4 uppercase tracking-wide">
          <span className="text-blue-600 mr-2">6.</span>Continuous Monitoring Assurance
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          {[
            { label: "Status", value: data.monitoring_active ? "ACTIVE" : "INACTIVE", color: data.monitoring_active ? "text-emerald-600" : "text-red-600" },
            { label: "Active Monitors", value: data.monitors_count },
            { label: "Alerts Tracked", value: data.alerts_tracked },
            { label: "Vulnerabilities Tracked", value: data.vulnerabilities_tracked },
          ].map((card, i) => (
            <div key={i} className="bg-slate-50 rounded p-4 text-center">
              <div className={`text-2xl font-bold ${card.color || "text-slate-800"}`}>{card.value}</div>
              <div className="text-xs text-slate-500 uppercase mt-1">{card.label}</div>
            </div>
          ))}
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded p-4 text-sm text-slate-700 leading-relaxed">
          {data.monitoring_active
            ? "Active continuous monitoring detected. Vulnerabilities are tracked through remediation lifecycle with assigned responsibility. Exposure is not static — the organization demonstrates ongoing risk management commitment, reducing actuarial uncertainty."
            : "No active continuous monitoring detected. Risk exposure assessment reflects point-in-time evaluation only. Absence of monitoring increases actuarial uncertainty regarding risk trajectory between assessment periods."}
        </div>
      </div>

      {/* Section 6.5: Vulnerability Exposure Time Analysis */}
      {data.vulnerability_exposure && data.vulnerability_exposure.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6" data-testid="uw-section-exposure">
          <h3 className="text-lg font-bold text-slate-800 border-b-2 border-slate-800 pb-2 mb-4 uppercase tracking-wide">
            <span className="text-blue-600 mr-2">6.5</span>Vulnerability Exposure Time Analysis
          </h3>

          {/* Summary cards */}
          {data.exposure_summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
              <div className="bg-slate-50 rounded p-4 text-center">
                <div className="text-2xl font-bold text-slate-800">{data.exposure_summary.avg_days_exposed}d</div>
                <div className="text-xs text-slate-500 uppercase mt-1">Avg. Days Exposed</div>
              </div>
              <div className="bg-slate-50 rounded p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{data.exposure_summary.max_days_exposed}d</div>
                <div className="text-xs text-slate-500 uppercase mt-1">Max Exposure</div>
              </div>
              <div className="bg-slate-50 rounded p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{data.exposure_summary.critical_long_exposure}</div>
                <div className="text-xs text-slate-500 uppercase mt-1">Critical &gt; 30d</div>
              </div>
              <div className="bg-slate-50 rounded p-4 text-center">
                <div className="text-2xl font-bold text-orange-600">{data.exposure_summary.high_long_exposure}</div>
                <div className="text-xs text-slate-500 uppercase mt-1">High &gt; 60d</div>
              </div>
            </div>
          )}

          {/* Exposure interpretation for underwriters */}
          <div className="bg-amber-50 border border-amber-200 border-l-4 border-l-orange-500 rounded p-4 mb-5">
            <p className="text-sm text-slate-700 leading-relaxed">
              <strong>Underwriting Insight:</strong> {data.exposure_summary && data.exposure_summary.avg_days_exposed > 60
                ? "Extended vulnerability exposure windows significantly increase actuarial risk. Average exposure exceeds 60 days, indicating delayed remediation cycles that elevate breach probability."
                : data.exposure_summary && data.exposure_summary.avg_days_exposed > 30
                ? "Moderate vulnerability exposure detected. Average remediation window exceeds 30 days. While not immediately critical, this indicates room for improvement in patch management processes."
                : "Vulnerability exposure windows are within acceptable ranges. Remediation cycles appear responsive, reducing time-based risk accumulation."}
            </p>
          </div>

          {/* Vulnerability table with exposure */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800 text-white text-xs uppercase tracking-wider">
                  <th className="px-4 py-3 text-left">Vulnerability</th>
                  <th className="px-4 py-3 text-left">Severity</th>
                  <th className="px-4 py-3 text-center">Days Exposed</th>
                  <th className="px-4 py-3 text-left">First Seen</th>
                  <th className="px-4 py-3 text-center">Risk Level</th>
                </tr>
              </thead>
              <tbody>
                {data.vulnerability_exposure
                  .filter(v => v.days_exposed != null)
                  .sort((a, b) => (b.days_exposed || 0) - (a.days_exposed || 0))
                  .slice(0, 15)
                  .map((v, i) => {
                    const isLong = v.days_exposed > 90;
                    const isMed = v.days_exposed > 30;
                    const sevColor = v.severity === 'critical' ? 'bg-red-600' : v.severity === 'high' ? 'bg-orange-500' : v.severity === 'medium' ? 'bg-yellow-500' : 'bg-emerald-500';
                    const exposureColor = isLong ? 'text-red-700 font-bold' : isMed ? 'text-orange-600 font-semibold' : 'text-slate-700';
                    const riskLabel = v.severity === 'critical' && v.days_exposed > 30 ? 'URGENT' : v.severity === 'high' && v.days_exposed > 60 ? 'HIGH' : v.days_exposed > 90 ? 'ELEVATED' : 'MONITOR';
                    const riskColor = riskLabel === 'URGENT' ? 'bg-red-100 text-red-700' : riskLabel === 'HIGH' ? 'bg-orange-100 text-orange-700' : riskLabel === 'ELEVATED' ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-600';
                    return (
                      <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"} data-testid={`uw-exposure-row-${i}`}>
                        <td className="px-4 py-3 font-medium text-slate-800 max-w-xs truncate">{v.title}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold text-white uppercase ${sevColor}`}>{v.severity}</span>
                        </td>
                        <td className={`px-4 py-3 text-center ${exposureColor}`}>{v.days_exposed}d</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{v.first_seen ? new Date(v.first_seen).toLocaleDateString() : 'N/A'}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${riskColor}`}>{riskLabel}</span>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Section 7: Risk Mitigation Roadmap */}
      <div className="bg-white rounded-lg shadow p-6" data-testid="uw-section-7">
        <h3 className="text-lg font-bold text-slate-800 border-b-2 border-slate-800 pb-2 mb-4 uppercase tracking-wide">
          <span className="text-blue-600 mr-2">7.</span>Risk Mitigation Roadmap
        </h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-800 text-white text-xs uppercase tracking-wider">
              <th className="px-4 py-3 text-left">Priority Action</th>
              <th className="px-4 py-3 text-left">Risk Reduced</th>
              <th className="px-4 py-3 text-left">Expected Effect</th>
            </tr>
          </thead>
          <tbody>
            {(data.mitigation_roadmap || []).map((m, i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                <td className="px-4 py-3 font-medium text-slate-800">{m.priority}</td>
                <td className="px-4 py-3 text-slate-600">{m.risk_reduced}</td>
                <td className="px-4 py-3 text-slate-600">{m.expected_effect}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Section 8: Methodology */}
      <div className="bg-white rounded-lg shadow p-6" data-testid="uw-section-8">
        <h3 className="text-lg font-bold text-slate-800 border-b-2 border-slate-800 pb-2 mb-4 uppercase tracking-wide">
          <span className="text-blue-600 mr-2">8.</span>Methodology
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed">{data.methodology_text}</p>
      </div>

      {/* Section 9: Disclaimer */}
      <div className="border-t border-slate-200 pt-4 mt-6" data-testid="uw-section-9">
        <p className="text-xs text-slate-400 italic leading-relaxed">{data.disclaimer}</p>
      </div>

      {/* Confidentiality */}
      <div className="text-center py-3">
        <span className="text-xs text-red-500 border border-red-300 px-4 py-1 rounded uppercase tracking-widest">
          {data.confidentiality_notice}
        </span>
      </div>
    </div>
  );
};

export default UnderwritingReport;
